import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { getBlob, ref } from "firebase/storage";
import { storage } from "config/firebase";
import {
  useSfwReveal,
  TapToReveal,
  SFW_BLUR_FILTER,
} from "components/BlurReveal";

// Album photos are no longer publicly readable, so a bare <img src=publicUrl>
// 403s. Fetch the bytes through the Firebase Storage SDK (which attaches the
// auth + App Check token) and render them as a blob URL instead.
//
// ponytail: session-scoped Map cache, object URLs are never revoked. Bounded by
// the number of distinct photos viewed in a session — fine here. If memory ever
// bites, revoke on a LRU/size cap.
const cache = new Map<string, string>();

export function useStorageImage(path?: string | null): string | null {
  const [url, setUrl] = useState<string | null>(() =>
    path ? cache.get(path) ?? null : null
  );

  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }
    const cached = cache.get(path);
    if (cached) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    getBlob(ref(storage, path))
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        cache.set(path, objectUrl);
        setUrl(objectUrl);
      })
      .catch(() => {
        // Leave url null so the caller's placeholder/skeleton stays shown.
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  return url;
}

interface StorageImageProps {
  path?: string | null;
  sx?: any;
  [prop: string]: any;
}

// Drop-in replacement for <Box component="img" src=...>, loading `path` via the
// SDK. Passes through sx and any img props (alt, onLoad, onClick, draggable...).
export default function StorageImage({
  path,
  sx,
  ...imgProps
}: StorageImageProps) {
  const url = useStorageImage(path);
  const { blurred, reveal } = useSfwReveal();

  // Default (SFW off): a bare img, identical to before -- no layout change.
  if (!blurred) {
    return <Box component="img" src={url ?? undefined} sx={sx} {...imgProps} />;
  }

  // SFW on: blur the image and overlay a tap target. The wrapper fills the sized
  // parent every StorageImage call site already provides (feed box, grid tile).
  return (
    <Box
      sx={{
        position: "relative",
        display: "block",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        component="img"
        src={url ?? undefined}
        sx={{ ...sx, filter: SFW_BLUR_FILTER }}
        {...imgProps}
      />
      <TapToReveal onReveal={reveal} />
    </Box>
  );
}
